// ─── src/chat/WorkspaceCenter.jsx ─────────────────────────────────────────────
import { useState, useRef, useEffect, useCallback } from "react";
import { useAura } from "../stores/auraCore";
import Icon from "./Icons";
import { submitAuraFeedback } from "../services/auraFeedback";
import { getOrCreateAnonId } from "../services/auraIdentity";
import { cleanDashes } from "../utils/cleanText";
import AuraLinkedText from "./AuraLinkedText";

const GOLD = "#d39a21";

const SMART_CHIPS = [
  "80 guests in Tuscany this autumn",
  "Historic black-tie venues under £30k",
  "Intimate elopement on the Amalfi Coast",
];

// These chips are buying signals, clicking them opens the enquiry form directly
// instead of sending a chat message. Never re-route through the LLM for these.
const ENQUIRY_CHIPS = new Set([
  'Send an enquiry',
  'Ask about pricing',
  'Check date availability',
  'Enquire directly',
  'Arrange a private viewing',
  'Speak to the venue team',
  'We would love to arrange a viewing',
  "Ask about what's included",
]);

// ── Theme token factory ────────────────────────────────────────────────────────
function getT(dark) {
  if (dark) return {
    centerBg:       "#1A1816",
    auraBubbleBg:   "#262220",
    auraBubbleBorder:"rgba(211,154,33,0.12)",
    auraBubbleText: "rgba(255,255,255,0.92)",
    userBubbleBg:   "#d39a21",
    userBubbleBorder:"transparent",
    userBubbleText: "#0D0B09",
    inputBg:        "rgba(255,248,230,0.05)",
    inputBorder:    "rgba(211,154,33,0.18)",
    inputText:      "rgba(255,255,255,0.92)",
    inputPlaceholder:"rgba(255,255,255,0.35)",
    hintText:       "rgba(255,255,255,0.3)",
    thumbActiveBg:  "rgba(211,154,33,0.15)",
    thumbActiveBorder:"rgba(211,154,33,0.4)",
    thumbHovBg:     "rgba(255,255,255,0.07)",
    thumbHovBorder: "rgba(255,255,255,0.2)",
    thumbIconColor: "rgba(255,255,255,0.55)",
    thumbIconActiveColor: "#d39a21",
    dividerLine:    "rgba(211,154,33,0.08)",
    scrollTrack:    "#1E1C1A",
    imgPreviewBg:   "rgba(255,248,230,0.05)",
    imgPreviewBorder:"rgba(211,154,33,0.15)",
  };
  return {
    centerBg:       "#FDFBF8",
    auraBubbleBg:   "#F5F0E8",
    auraBubbleBorder:"rgba(211,154,33,0.08)",
    auraBubbleText: "#1A1714",
    userBubbleBg:   "#1A1714",
    userBubbleBorder:"transparent",
    userBubbleText: "#FFFFFF",
    inputBg:        "#F8F6F2",
    inputBorder:    "#E5E0D8",
    inputText:      "#1A1714",
    inputPlaceholder:"rgba(26,23,20,0.35)",
    hintText:       "rgba(26,23,20,0.32)",
    thumbActiveBg:  "rgba(211,154,33,0.12)",
    thumbActiveBorder:"rgba(211,154,33,0.35)",
    thumbHovBg:     "rgba(0,0,0,0.05)",
    thumbHovBorder: "#D8D3CA",
    thumbIconColor: "rgba(26,23,20,0.45)",
    thumbIconActiveColor: "#9A6F1A",
    dividerLine:    "#EDEBE7",
    scrollTrack:    "#F0EDE8",
    imgPreviewBg:   "#F0ECE6",
    imgPreviewBorder:"#DDD8D0",
  };
}

export default function WorkspaceCenter({ darkMode, initialDraft }) {
  const messages           = useAura((s) => s.messages);
  const isTyping           = useAura((s) => s.isTyping);
  // PROTOTYPE (aura/streaming-prototype branch, project_aura_streaming_ux_improvement.md):
  // both are null whenever streaming isn't active (production today), in which case the
  // typing indicator below renders exactly as it always has.
  const streamStage        = useAura((s) => s.streamStage);
  const streamingText      = useAura((s) => s.streamingText);
  const sendMessage        = useAura((s) => s.sendMessage);
  const conversationMode   = useAura((s) => s.conversationMode);
  const activeVenueId      = useAura((s) => s.activeVenueId);
  const enquiredVenueIds   = useAura((s) => s.enquiredVenueIds);
  // True when the currently active venue has already received an enquiry
  const activeEnquired     = !!(activeVenueId && enquiredVenueIds.includes(activeVenueId));
  const T = getT(darkMode);

  const [input,    setInput]    = useState(initialDraft || "");
  const [attached, setAttached] = useState(null);  // { url, name }
  const [listening, setListening] = useState(false);

  const threadRef    = useRef(null);
  const inputRef     = useRef(null);
  const fileRef      = useRef(null);
  const sentDraftRef = useRef(false);

  // Auto-scroll
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, isTyping, streamingText]);

  // Auto-focus
  useEffect(() => { inputRef.current?.focus(); }, []);

  // If opened with a pre-filled query (from hero search bar), auto-send it
  useEffect(() => {
    if (!initialDraft?.trim() || sentDraftRef.current) return;
    sentDraftRef.current = true;
    const t = setTimeout(() => {
      sendMessage(initialDraft.trim());
      setInput("");
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim() && !attached) return;
    sendMessage(input.trim() || (attached ? `[Image: ${attached.name}]` : ""));
    setInput("");
    setAttached(null);
  }, [input, attached, sendMessage]);

  const handleKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAttached({ url, name: file.name });
    e.target.value = "";   // reset so same file can be picked again
  };

  const handleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) return;
    const rec = new SR();
    rec.lang = "en-GB";
    rec.interimResults = false;
    rec.onstart = () => setListening(true);
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput((prev) => (prev ? prev + " " + t : t));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
  };

  const canSend = input.trim().length > 0 || !!attached;

  return (
    <>
      <style>{`
        @keyframes ws-dot-pulse {
          0%, 80%, 100% { transform: scale(0); opacity: 0.35; }
          40%            { transform: scale(1); opacity: 1;    }
        }
        @keyframes ws-cursor-blink {
          50% { opacity: 0; }
        }
        .ws-thread::-webkit-scrollbar       { width: 4px; }
        .ws-thread::-webkit-scrollbar-track { background: ${T.scrollTrack}; }
        .ws-thread::-webkit-scrollbar-thumb { background: ${GOLD}; border-radius: 2px; }
        .ws-msg-row .ws-thumbs { opacity: 0.55; transition: opacity 0.2s ease; }
        .ws-msg-row:hover .ws-thumbs { opacity: 1; }
        .ws-thumbs.ws-thumbs--active { opacity: 1; }
      `}</style>

      <div style={{ height: "100%", display: "flex", flexDirection: "column", background: T.centerBg, transition: "background 0.3s ease" }}>

        {/* ── Thread ─────────────────────────────────────────────────────────── */}
        <div
          ref={threadRef}
          className="ws-thread"
          style={{ flex: 1, overflowY: "auto", padding: "28px 24px 16px", display: "flex", flexDirection: "column", gap: 16, justifyContent: messages.length <= 2 ? "center" : "flex-start" }}
        >
          {messages.map((m) => {
            // ── System note: compact gold divider, no bubble ──────────────────
            if (m._systemNote) {
              return (
                <div
                  key={m.id}
                  style={{
                    display:    "flex",
                    alignItems: "center",
                    gap:        10,
                    padding:    "2px 0",
                    opacity:    0.55,
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: `rgba(211,154,33,0.22)` }} />
                  <span style={{
                    fontFamily:    "var(--font-body)",
                    fontSize:      8,
                    fontWeight:    700,
                    letterSpacing: "1.6px",
                    textTransform: "uppercase",
                    color:         GOLD,
                    whiteSpace:    "nowrap",
                  }}>
                    {m._systemNoteText ?? m.text}
                  </span>
                  <div style={{ flex: 1, height: 1, background: `rgba(211,154,33,0.22)` }} />
                </div>
              );
            }

            return (
            <div
              key={m.id}
              className="ws-msg-row"
              style={{ display: "flex", flexDirection: m.from === "user" ? "row-reverse" : "row", alignItems: "flex-end", gap: 8 }}
            >
              {/* Aura avatar */}
              {m.from === "aura" && (
                <div
                  aria-hidden="true"
                  style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "rgba(211,154,33,0.1)",
                    border: "1px solid rgba(211,154,33,0.35)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, color: GOLD, flexShrink: 0, marginBottom: 2,
                  }}
                ><Icon name="sparkleMini" size={14} color={GOLD} /></div>
              )}

              {/* Bubble + feedback */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: m.from === "user" ? "flex-end" : "flex-start", maxWidth: "78%", gap: 4 }}>
                {/* Concierge intro header on first Aura message */}
                {m.from === "aura" && m.id === 0 && (
                  <div style={{
                    fontFamily:    "var(--font-heading-primary)",
                    fontSize:      11,
                    fontWeight:    500,
                    letterSpacing: "1.8px",
                    textTransform: "uppercase",
                    color:         GOLD,
                    opacity:       0.7,
                    marginBottom:  2,
                    paddingLeft:   2,
                  }}>
                    Your Private Wedding Concierge
                  </div>
                )}
                <div
                  style={{
                    padding: "13px 18px",
                    borderRadius:  m.from === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    background:    m.from === "user" ? T.userBubbleBg  : T.auraBubbleBg,
                    border:        `1px solid ${m.from === "user" ? T.userBubbleBorder : T.auraBubbleBorder}`,
                    color:         m.from === "user" ? T.userBubbleText : T.auraBubbleText,
                    fontFamily:   "var(--font-body)",
                    fontSize:      14.5,
                    lineHeight:    1.65,
                    boxShadow:     m.from === "user" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    transition:   "background 0.3s ease, color 0.3s ease, border-color 0.3s ease",
                  }}
                >
                  {m.from === "aura" ? <AuraLinkedText text={cleanDashes(m.text)} /> : m.text}
                </div>

                {/* Feedback bar, Aura messages only, not the greeting */}
                {m.from === "aura" && m.id !== 0 && (
                  <FeedbackBar message={m} darkMode={darkMode} T={T} messages={messages} />
                )}

                {/* Enquiry transition CTA, surfaces when Aura detects booking intent */}
                {m.from === "aura" && conversationMode === "enquiry_transition" && m.id === messages.filter(x => x.from === "aura").at(-1)?.id && (
                  <div style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          8,
                    padding:      "9px 14px",
                    marginTop:    4,
                    background:   "rgba(211,154,33,0.07)",
                    border:       "1px solid rgba(211,154,33,0.3)",
                    borderRadius: 8,
                  }}>
                    <Icon name="sparkleMini" size={12} color={GOLD} />
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: "rgba(211,154,33,0.9)", lineHeight: 1.4 }}>
                      Use the <strong style={{ color: GOLD }}>Enquire</strong> button on a venue card to send your details directly to their team.
                    </span>
                  </div>
                )}

                {/* Follow-up prompt chips, only on the last Aura message */}
                {m.from === "aura" && m.follow_up_prompts?.length > 0 && m.id === messages.filter(x => x.from === "aura").at(-1)?.id && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 2, marginTop: 2 }}>
                    {m.follow_up_prompts.filter((prompt) =>
                      // Hide buying-signal chips once the active venue has been enquired.
                      // They reappear automatically when the user focuses a new venue.
                      !(activeEnquired && ENQUIRY_CHIPS.has(prompt))
                    ).map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          if (ENQUIRY_CHIPS.has(prompt)) {
                            // Buying signal, open the enquiry form for the active venue.
                            // Never send this through the LLM; it is a conversion action.
                            window.dispatchEvent(new CustomEvent('lwd:aura:open-enquiry'));
                          } else {
                            sendMessage(prompt);
                          }
                        }}
                        style={{
                          background:   "transparent",
                          border:       "1px solid rgba(211,154,33,0.28)",
                          borderRadius: 20,
                          padding:      "5px 12px",
                          cursor:       "pointer",
                          fontFamily:   "var(--font-body)",
                          fontSize:     11.5,
                          // Gold-on-cream failed to read in light mode. The gold ring
                          // still marks these as Aura's suggestions; the label itself is
                          // now text-coloured, so a chip is legible before it is decorative.
                          color:        darkMode ? "rgba(211,154,33,0.75)" : "rgba(28,28,28,0.72)",
                          letterSpacing: "0.02em",
                          transition:   "border-color 0.2s, color 0.2s, background 0.2s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(211,154,33,0.6)"; e.currentTarget.style.color = darkMode ? GOLD : "rgba(28,28,28,0.95)"; e.currentTarget.style.background = "rgba(211,154,33,0.06)"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(211,154,33,0.28)"; e.currentTarget.style.color = darkMode ? "rgba(211,154,33,0.75)" : "rgba(28,28,28,0.72)"; e.currentTarget.style.background = "transparent"; }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}

              </div>
            </div>
          );
          })}

          {/* Smart suggestion chips, only on initial greeting state */}
          {messages.length === 1 && messages[0].id === 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingLeft: 38, marginTop: -4 }}>
              {SMART_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => sendMessage(chip)}
                  style={{
                    background:    darkMode ? "rgba(211,154,33,0.08)" : "rgba(211,154,33,0.07)",
                    border:        `1px solid ${darkMode ? "rgba(211,154,33,0.18)" : "rgba(211,154,33,0.15)"}`,
                    borderRadius:  20,
                    padding:       "8px 16px",
                    fontFamily:    "var(--font-body)",
                    fontSize:      12.5,
                    color:         darkMode ? "rgba(255,255,255,0.65)" : "rgba(26,23,20,0.55)",
                    cursor:        "pointer",
                    lineHeight:    1.3,
                    transition:    "all 0.2s",
                    whiteSpace:    "nowrap",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = darkMode ? "rgba(211,154,33,0.18)" : "rgba(211,154,33,0.15)"; e.currentTarget.style.color = darkMode ? "rgba(255,255,255,0.65)" : "rgba(26,23,20,0.55)"; }}
                >{chip}</button>
              ))}
            </div>
          )}

          {/* Typing indicator — streamStage/streamingText are only ever non-null on the
              aura/streaming-prototype branch with VITE_AURA_STREAM_URL set; production
              falls through to the original three-dot indicator, unchanged. */}
          {isTyping && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(211,154,33,0.1)", border: "1px solid rgba(211,154,33,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: GOLD, flexShrink: 0 }}><Icon name="sparkleMini" size={14} color={GOLD} /></div>
              {streamingText ? (
                <div style={{ padding: "12px 16px", borderRadius: "18px 18px 18px 4px", background: T.auraBubbleBg, maxWidth: "78%", fontSize: 14.5, lineHeight: 1.5, color: T.auraBubbleText, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  {streamingText}
                  <span style={{ display: "inline-block", width: 6, height: 13, marginLeft: 2, background: GOLD, verticalAlign: "text-bottom", animation: "ws-cursor-blink 0.8s steps(1) infinite" }} />
                </div>
              ) : streamStage ? (
                <div style={{ padding: "12px 16px", borderRadius: "18px 18px 18px 4px", background: T.auraBubbleBg, display: "flex", gap: 8, alignItems: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD, display: "inline-block", animation: "ws-dot-pulse 1.4s ease infinite" }} />
                  <span style={{ fontSize: 13, color: darkMode ? "rgba(255,255,255,0.65)" : "rgba(26,23,20,0.6)" }}>{streamStage.label}</span>
                </div>
              ) : (
                <div style={{ padding: "12px 16px", borderRadius: "18px 18px 18px 4px", background: T.auraBubbleBg, display: "flex", gap: 5, alignItems: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD, display: "inline-block", animation: `ws-dot-pulse 1.4s ease ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Divider ─────────────────────────────────────────────────────────── */}
        <div style={{ height: 1, background: T.dividerLine, flexShrink: 0 }} />

        {/* ── Input area ──────────────────────────────────────────────────────── */}
        <div style={{ padding: "12px 20px 14px", background: T.centerBg, flexShrink: 0, transition: "background 0.3s ease" }}>

          {/* Image preview strip */}
          {attached && (
            <div
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:           10,
                marginBottom:  10,
                background:    T.imgPreviewBg,
                border:       `1px solid ${T.imgPreviewBorder}`,
                borderRadius:  10,
                padding:      "8px 12px",
              }}
            >
              <img
                src={attached.url}
                alt="attachment preview"
                style={{ width: 48, height: 48, objectFit: "cover", borderRadius: "var(--lwd-radius-image)", flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: T.inputText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {attached.name}
                </div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 10, color: T.hintText }}>
                  Image attached
                </div>
              </div>
              <button
                onClick={() => setAttached(null)}
                aria-label="Remove attachment"
                style={{ background: "none", border: "none", cursor: "pointer", color: T.hintText, fontSize: 16, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}
              >×</button>
            </div>
          )}

          <div
            style={{
              display:      "flex",
              gap:           8,
              alignItems:   "flex-end",
              background:    T.inputBg,
              border:       `1px solid ${T.inputBorder}`,
              borderRadius:  14,
              padding:      "10px 10px 10px 10px",
              boxShadow:    "0 1px 8px rgba(0,0,0,0.05)",
              transition:   "background 0.3s, border-color 0.3s",
            }}
          >
            {/* Hidden file input */}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {/* Upload button */}
            <button
              onClick={() => fileRef.current?.click()}
              aria-label="Attach image"
              title="Attach image"
              style={{
                width:          38,
                height:         38,
                flexShrink:     0,
                borderRadius:   10,
                background:    "none",
                border:        `1px solid ${T.inputBorder}`,
                color:          T.hintText,
                cursor:        "pointer",
                fontSize:       18,
                display:       "flex",
                alignItems:    "center",
                justifyContent: "center",
                transition:    "all 0.2s",
                alignSelf:     "flex-end",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.inputBorder; e.currentTarget.style.color = T.hintText; }}
            >
              <Icon name="paperclip" size={16} />
            </button>

            {/* Voice input */}
            {(window.SpeechRecognition || window.webkitSpeechRecognition) && (
              <button
                onClick={handleVoice}
                aria-label={listening ? "Listening..." : "Voice input"}
                title={listening ? "Listening..." : "Voice input"}
                style={{
                  width:          38,
                  height:         38,
                  flexShrink:     0,
                  borderRadius:   10,
                  background:     listening ? "rgba(211,154,33,0.15)" : "none",
                  border:         `1px solid ${listening ? GOLD : T.inputBorder}`,
                  color:          listening ? GOLD : T.hintText,
                  cursor:        "pointer",
                  fontSize:       18,
                  display:       "flex",
                  alignItems:    "center",
                  justifyContent: "center",
                  transition:    "all 0.2s",
                  alignSelf:     "flex-end",
                  animation:      listening ? "ws-dot-pulse 1.4s ease infinite" : "none",
                }}
                onMouseEnter={(e) => { if (!listening) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; } }}
                onMouseLeave={(e) => { if (!listening) { e.currentTarget.style.borderColor = T.inputBorder; e.currentTarget.style.color = T.hintText; } }}
              >
                <Icon name="mic" size={16} />
              </button>
            )}

            {/* Text input */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Guest count, style, budget, destination…"
              rows={1}
              style={{
                flex:       1,
                background: "none",
                border:     "none",
                resize:     "none",
                fontFamily: "var(--font-body)",
                fontSize:    14,
                color:       T.inputText,
                outline:    "none",
                lineHeight:  1.5,
                maxHeight:   120,
                overflowY:  "auto",
                caretColor:  GOLD,
              }}
            />

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send message"
              style={{
                width:          40,
                height:         40,
                flexShrink:     0,
                borderRadius:   10,
                background:     canSend ? GOLD : "rgba(211,154,33,0.15)",
                border:         "none",
                color:          canSend ? "#0f0d0a" : "rgba(211,154,33,0.4)",
                cursor:         canSend ? "pointer" : "default",
                fontSize:       18,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                transition:     "all 0.2s",
                alignSelf:      "flex-end",
                fontWeight:      700,
              }}
            ><Icon name="arrowUp" size={18} strokeWidth={2.5} /></button>
          </div>

          {/* Hints */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, flexWrap: "wrap", gap: 4 }}>
            <span style={{ fontFamily: "var(--font-body)", fontSize: 10, color: T.hintText }}>
              Enter to send · Shift+Enter for new line
            </span>
            <span style={{ fontFamily: "var(--font-body)", fontSize: 9, color: T.hintText, letterSpacing: "0.2px", opacity: 0.65 }}>
              Aura can make mistakes
            </span>
          </div>
        </div>

      </div>
    </>
  );
}

// ── Thumb button ───────────────────────────────────────────────────────────────
function ThumbBtn({ icon, active, label, onClick, T }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:   active ? T.thumbActiveBg : (hov ? T.thumbHovBg : "none"),
        border:       `1px solid ${active ? T.thumbActiveBorder : (hov ? T.thumbHovBorder : "transparent")}`,
        borderRadius:  20,
        padding:      "2px 7px",
        cursor:        "pointer",
        fontSize:       12,
        lineHeight:     1,
        color:         active ? T.thumbIconActiveColor : T.thumbIconColor,
        transition:    "all 0.15s",
        display:       "flex",
        alignItems:    "center",
      }}
    >{icon}</button>
  );
}

// ── Feedback bar ───────────────────────────────────────────────────────────────
// Renders below each Aura response (except the greeting).
// Phase 1 signals: helpful / not_helpful
// Phase 2 (expands on not_helpful): too_expensive / wrong_style / wrong_region / not_relevant
//
// UX contract:
//   • Thumbs up  → immediate submit + "Noted, thank you" state
//   • Not quite  → expand sub-signal pills
//   • Sub-signal → submit with sub-signal + "Noted" state
//   • One submission per message, no re-voting

const SUB_SIGNALS = [
  { key: "too_expensive",  label: "Too expensive"  },
  { key: "wrong_style",    label: "Not our style"  },
  { key: "wrong_region",   label: "Wrong region"   },
  { key: "not_relevant",   label: "Not relevant"   },
];

function FeedbackBar({ message, darkMode, T, messages }) {
  const sessionId = useAura((s) => s.session.id);
  const [phase,    setPhase]    = useState("idle");   // idle | expanded | done
  const [submitting, setSubmitting] = useState(false);

  // Derive turn number from message position in thread
  const turnNumber = messages
    ? messages.filter(m => m.from === "aura" && m.id <= message.id).length
    : null;

  const submit = async (signal, subSignal = null) => {
    if (submitting || phase === "done") return;
    setSubmitting(true);
    const anonId = getOrCreateAnonId();
    const intent = message._intent ?? {};
    await submitAuraFeedback({
      session_id:    sessionId,
      query_id:      message._query_id ?? null,
      listing_ids:   message._listingIds ?? [],
      signal,
      sub_signal:    subSignal,
      anonymous_id:  anonId,
      turn_number:   turnNumber,
      intent_region: intent.region_slug ?? null,
      intent_styles: intent.styles ?? [],
    });
    setPhase("done");
    setSubmitting(false);
  };

  if (phase === "done") {
    return (
      <div className="ws-thumbs ws-thumbs--active" style={{ paddingLeft: 4, height: 22, display: "flex", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: GOLD, fontFamily: "var(--font-body)", opacity: 0.75 }}>
          Noted, thank you
        </span>
      </div>
    );
  }

  return (
    <div className={`ws-thumbs${phase !== "idle" ? " ws-thumbs--active" : ""}`} style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 4 }}>
      {/* Primary row */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <ThumbBtn
          icon={<Icon name="thumbUp" size={12} />}
          active={false}
          label="Helpful"
          onClick={() => submit("helpful")}
          T={T}
        />
        <ThumbBtn
          icon={<Icon name="thumbDown" size={12} />}
          active={phase === "expanded"}
          label="Not quite"
          onClick={() => setPhase(p => p === "expanded" ? "idle" : "expanded")}
          T={T}
        />
        {phase === "expanded" && (
          <span style={{ fontSize: 10, color: darkMode ? "rgba(255,255,255,0.35)" : "rgba(26,23,20,0.4)", fontFamily: "var(--font-body)", marginLeft: 2 }}>
            What missed the mark?
          </span>
        )}
      </div>

      {/* Sub-signal pills, only when expanded */}
      {phase === "expanded" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {SUB_SIGNALS.map(({ key, label }) => (
            <SubSignalPill
              key={key}
              label={label}
              darkMode={darkMode}
              onClick={() => submit("not_helpful", key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubSignalPill({ label, darkMode, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:    hov
          ? (darkMode ? "rgba(211,154,33,0.12)" : "rgba(211,154,33,0.09)")
          : "transparent",
        border:       `1px solid ${hov ? "rgba(211,154,33,0.45)" : "rgba(211,154,33,0.22)"}`,
        borderRadius:  20,
        padding:      "3px 10px",
        cursor:        "pointer",
        fontFamily:   "var(--font-body)",
        fontSize:      10.5,
        color:         hov ? GOLD : "rgba(211,154,33,0.65)",
        transition:   "all 0.15s",
        letterSpacing: "0.01em",
      }}
    >{label}</button>
  );
}
